/** Dondefue brand colour catalogue — 32 named swatches.
 *
 *  Single source of truth for all colour pickers:
 *    • embed  attach*Color rows (shape fill, text, bg, marker)
 *    • admin  closed-palette popovers (marker, background)
 *    • presets brandPalette arrays
 *
 *  Order matches the official colour card (image reference 2026-05-20):
 *  warm spectrum → pinks / purples → blues → greens → neutrals → achromatics.
 */
export type BrandSwatch = { id: string; label: string; hex: string };

export const BRAND_PALETTE: BrandSwatch[] = [
  // ── Warm spectrum ─────────────────────────────────────────────────
  { id: 'amarillo',      label: 'Amarillo',      hex: '#F4B400' },
  { id: 'naranja',       label: 'Naranja',       hex: '#D07830' },
  { id: 'rojo',          label: 'Rojo',          hex: '#C83028' },
  { id: 'borgona',       label: 'Borgoña',       hex: '#781E28' },
  { id: 'fucsia',        label: 'Fucsia',        hex: '#D63E82' },
  // ── Pinks / purples ───────────────────────────────────────────────
  { id: 'rosaceo',       label: 'Rosáceo',       hex: '#CC7070' },
  { id: 'rosa',          label: 'Rosa',          hex: '#E2A0A0' },
  { id: 'violeta',       label: 'Violeta',       hex: '#7A3290' },
  { id: 'berenjena',     label: 'Berenjena',     hex: '#3C1640' },
  { id: 'gris_paloma',   label: 'Gris paloma',   hex: '#9CACC0' },
  // ── Blues ─────────────────────────────────────────────────────────
  { id: 'azul_cielo',    label: 'Azul cielo',    hex: '#76ACDC' },
  { id: 'azul_agua',     label: 'Azul agua',     hex: '#68C4CC' },
  { id: 'turquesa',      label: 'Turquesa',      hex: '#30A4B4' },
  { id: 'azul_royal',    label: 'Azul royal',    hex: '#1C3AA8' },
  { id: 'blue_jack',     label: 'Blue jack',     hex: '#181E48' },
  { id: 'medianoche',    label: 'Medianoche',    hex: '#080E1C' },
  { id: 'azul_grisaceo', label: 'Azul grisáceo', hex: '#485870' },
  { id: 'aguamarina',    label: 'Aguamarina',    hex: '#1C5E5E' },
  { id: 'azul_cobalto',  label: 'Azul cobalto',  hex: '#08082A' },
  // ── Greens ────────────────────────────────────────────────────────
  { id: 'menta',         label: 'Menta',         hex: '#7CCC9A' },
  { id: 'bosque',        label: 'Bosque',        hex: '#326238' },
  { id: 'verde_botella', label: 'Verde botella', hex: '#1A4228' },
  { id: 'caqui',         label: 'Caqui',         hex: '#504E28' },
  // ── Neutrals / earth tones ────────────────────────────────────────
  { id: 'crema',         label: 'Crema',         hex: '#F4ECD0' },
  { id: 'papel_antiguo', label: 'Papel antiguo', hex: '#CCBA8A' },
  { id: 'arcilla',       label: 'Arcilla',       hex: '#C09A68' },
  { id: 'marron',        label: 'Marrón',        hex: '#582A12' },
  { id: 'duty_blue',     label: 'Duty blue',     hex: '#4C607A' },
  { id: 'gris',          label: 'Gris',          hex: '#969698' },
  { id: 'antracita',     label: 'Antracita',     hex: '#28282E' },
  // ── Achromatics ───────────────────────────────────────────────────
  { id: 'negro',         label: 'Negro',         hex: '#0A0A0A' },
  { id: 'blanco',        label: 'Blanco',        hex: '#FFFFFF' },
];

/** Hex-only array — use when only raw colour values are needed. */
export const BRAND_PALETTE_HEXES: string[] = BRAND_PALETTE.map((c) => c.hex);

/** O(1) hex → swatch lookup (lowercase hex key). */
export const BRAND_PALETTE_BY_HEX: Map<string, BrandSwatch> =
  new Map(BRAND_PALETTE.map((c) => [c.hex.toLowerCase(), c]));

/** Subset principal (6 colores) que se muestra siempre inline en los
 *  pickers compactos.  Cubre las necesidades del 80% de los casos
 *  (achromatics + un cálido + un frío + un neutro) y desbloquea el
 *  resto detrás de un botón "+" que abre un modal con la paleta
 *  completa.  Elegidos por uso típico en posters / gift shop. */
export const BRAND_PALETTE_PRIMARY: BrandSwatch[] = [
  BRAND_PALETTE.find((c) => c.id === 'negro')!,
  BRAND_PALETTE.find((c) => c.id === 'blanco')!,
  BRAND_PALETTE.find((c) => c.id === 'rojo')!,
  BRAND_PALETTE.find((c) => c.id === 'azul_royal')!,
  BRAND_PALETTE.find((c) => c.id === 'bosque')!,
  BRAND_PALETTE.find((c) => c.id === 'arcilla')!,
];

/** Hex-only quick-pick subset. */
export const BRAND_PALETTE_PRIMARY_HEXES: string[] =
  BRAND_PALETTE_PRIMARY.map((c) => c.hex);

/** Provider for merchant-defined brand swatches.  The embed sets this
 *  at boot (apps/embed/src/index.ts → registerBrandSwatchesProvider)
 *  with a function that reads from the personalizer-settings bridge.
 *  Admin call-sites leave it unset → returns [].  This indirection
 *  keeps `@personalizer/ui-rows` decoupled from the embed's settings
 *  module (which depends on `ui-rows`, not the reverse).
 *
 *  Returns: validated, deduped, lowercase-key-comparable hex[].  The
 *  provider is consulted on every picker mount, so flipping the
 *  setting in /admin/personalizer reflects on the next picker open
 *  (no need to re-mount the entire overlay). */
let _brandSwatchesProvider: (() => string[]) | null = null;
export function registerBrandSwatchesProvider(fn: () => string[]): void {
  _brandSwatchesProvider = fn;
}
export function getMerchantBrandSwatches(): string[] {
  try { return _brandSwatchesProvider?.() ?? []; }
  catch { return []; }
}
